// ═══════════════════════════════════════════════════════════════
// job-number — per-mechanic sequential document numbers.
//
// One number per JOB, allocated when the job is created (not when
// the estimate is sent) so every ticket has an identity from the
// moment it exists and job-count / per-month stats can key off it.
//
// The same digits carry through the whole life of the ticket:
//
//   job.jobNumber = 1042  →  estimate reads  EST-1042
//                         →  invoice  reads  INV-1042
//
// so a customer holding an invoice can be traced straight back to
// the estimate they approved, with no lookup table.
//
// The counter lives at users/{uid}/counters/jobNumber, which means
// every mechanic gets an independent sequence — one shop can never
// infer another's job volume from a gap in their own numbers. It's
// covered by the existing users/{uid}/{document=**} rule, so this
// needs no firestore.rules change.
//
// Digital only. Paper tickets are deliberately a separate series
// (start those with a letter or a wildly different range).
// ═══════════════════════════════════════════════════════════════
import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase.js';
import { mechanicRoot } from '../database.js';

// Starting at 1 tells every customer they're job number one.
export const FIRST_JOB_NUMBER = 1001;

export function jobNumberCounterDoc() {
  return doc(mechanicRoot(), 'counters', 'jobNumber');
}

// Read the stored `next` defensively — a hand-edited or corrupted
// counter should never hand back NaN or walk the sequence backwards.
function nextFrom(snap) {
  const stored = snap.exists() ? Number(snap.data()?.next) : NaN;
  return Number.isFinite(stored) && stored >= FIRST_JOB_NUMBER
    ? Math.floor(stored)
    : FIRST_JOB_NUMBER;
}

// Claim the next number. Transactional, so two devices creating jobs
// at the same moment can't land on the same digits.
export async function allocateJobNumber() {
  const ref = jobNumberCounterDoc();
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const value = nextFrom(snap);
    tx.set(ref, { next: value + 1, updatedAt: serverTimestamp() }, { merge: true });
    return value;
  });
}

// Claim `count` consecutive numbers in one transaction; returns the
// first. Used by the Settings backfill so numbering legacy jobs is a
// single round trip instead of N.
export async function allocateJobNumberBlock(count) {
  const n = Math.max(0, Math.floor(Number(count) || 0));
  if (n === 0) return null;
  const ref = jobNumberCounterDoc();
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const start = nextFrom(snap);
    tx.set(ref, { next: start + n, updatedAt: serverTimestamp() }, { merge: true });
    return start;
  });
}

// Never let numbering failure block job creation.
//
// Firestore transactions require connectivity — they do NOT queue
// offline the way a plain write does. A mechanic standing in a
// driveway with one bar would otherwise be unable to create a job at
// all. So allocation is best-effort: on failure the job is created
// unnumbered and the Settings backfill can assign it later.
export async function allocateJobNumberSafe() {
  try {
    return await allocateJobNumber();
  } catch (e) {
    console.error('Job number allocation failed — creating job unnumbered:', e);
    return null;
  }
}

// ─── Display ────────────────────────────────────────────────────
// Every formatter returns '' for a missing number so legacy jobs
// (created before numbering shipped) render clean rather than
// showing "EST-null".
// Coerce a stored value to a real job number, or null.
//
// Careful: Number(null) and Number('') are both 0, and 0 is finite —
// so a bare Number.isFinite() check would render a job whose number
// allocation failed (jobNumber: null) as "EST-0". Reject the empty
// values explicitly before coercing.
function toJobNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function hasJobNumber(job) {
  return toJobNumber(job?.jobNumber) !== null;
}

export function formatJobNumber(jobNumber) {
  const n = toJobNumber(jobNumber);
  return n === null ? '' : String(n);
}

export function formatEstimateNumber(jobNumber) {
  const n = formatJobNumber(jobNumber);
  return n ? `EST-${n}` : '';
}

export function formatInvoiceNumber(jobNumber) {
  const n = formatJobNumber(jobNumber);
  return n ? `INV-${n}` : '';
}

// Which label a job should wear given where it is in its life. Draft
// and sent jobs are estimates; once invoiced it's an invoice.
export function formatDocumentNumber(job) {
  if (!hasJobNumber(job)) return '';
  const invoiced = job.status === 'invoiced' || job.status === 'paid' || !!job.invoiceShareId;
  return invoiced
    ? formatInvoiceNumber(job.jobNumber)
    : formatEstimateNumber(job.jobNumber);
}
